import React, {useEffect, useRef, useState} from "react"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../services/auth"
import jsonMergePatch from "json-merge-patch"
import Icon from "../Icon/Icon"
import classNames from "classnames"
import {ErrorDocument} from "../../resources/common"
import {Result} from "neverthrow"

export type Tag = {key: string; value: string}

type CloseFunc = () => void
type RefreshFunc = () => void

const MAX_TAGS = 30
const MAX_TAG_KEY_LENGTH = 128
const MAX_TAG_VALUE_LENGTH = 256
const modalCloseDelay = 1000

interface EditTagsModalProps {
	originalTags: {[key: string]: string}
	close: CloseFunc
	refresh: RefreshFunc
	updateTags: (accessToken: string, tags: {[key: string]: string | null}) => Promise<Result<unknown, ErrorDocument>>
}

function closeAndRefresh(refresh: RefreshFunc, close: CloseFunc, delay = 0) {
	return function () {
		setTimeout(() => {
			close()
			refresh()
		}, delay)
	}
}

interface TagPillProps {
	tag: Tag
	onRemove: () => void
}

function TagPill({tag, onRemove}: TagPillProps) {
	return (
		<div className="edit-tags-modal-tag-pill">
			<span className="edit-tags-modal-tag-pill-key">{tag.key}</span>
			<span className="edit-tags-modal-tag-pill-value">{tag.value}</span>
			<button type="button" className="edit-tags-modal-tag-pill-remove" onClick={onRemove}>
				<Icon icon="delete-close-x" size={12} />
			</button>
		</div>
	)
}

function Modal({originalTags, close, refresh, updateTags}: EditTagsModalProps) {
	const accessToken = useAccessToken()
	const [state, update] = useAsyncResultIdle(updateTags)
	const [tags, setTags] = useState<Tag[]>(Object.entries(originalTags).map(([key, value]) => ({key, value})))
	const [newTagKey, setNewTagKey] = useState("")
	const [newTagValue, setNewTagValue] = useState("")
	const [isAddingTag, setIsAddingTag] = useState(false)
	const keyInputRef = useRef<HTMLInputElement>(null)

	const isMaxTagsReached = tags.length >= MAX_TAGS
	const hasError = isMaxTagsReached && isAddingTag

	useEffect(() => {
		if (isAddingTag && keyInputRef.current) {
			keyInputRef.current.focus()
		}
	}, [isAddingTag])

	const handleRemoveTag = (index: number) => {
		setTags((prevTags) => prevTags.filter((_, i) => i !== index))
	}

	const handleAddTag = () => {
		if (newTagKey.trim() && newTagValue.trim()) {
			const newTag = {key: newTagKey.trim(), value: newTagValue.trim()}
			setTags((prevTags) => [...prevTags, newTag])
			setNewTagKey("")
			setNewTagValue("")
			setIsAddingTag(false)
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault()
			handleAddTag()
		} else if (e.key === "Escape") {
			setIsAddingTag(false)
			setNewTagKey("")
			setNewTagValue("")
		}
	}

	const handleSubmit = () => {
		let finalTags = [...tags]
		if (newTagKey.trim() && newTagValue.trim()) {
			finalTags = [...finalTags, {key: newTagKey.trim(), value: newTagValue.trim()}]
		}

		const newTagsObject = finalTags.reduce(
			(acc, tag) => ({...acc, [tag.key]: tag.value}),
			{} as {[key: string]: string}
		)
		const patch = jsonMergePatch.generate(originalTags, newTagsObject) as {[key: string]: string | null}

		if (Object.keys(patch).length === 0) {
			close()
			return
		}

		update(accessToken, patch)
	}

	return (
		<AsyncResultModal
			title="Edit Tags"
			classname="edit-tags-modal"
			enableClickAway={false}
			onSubmit={handleSubmit}
			close={close}
			state={state}
			buttonClassname="is-black"
			buttonText="Save"
			cancelButtonText="Cancel"
			cancelButtonClassname="is-light"
			successText="Tags updated successfully"
			errorToText={() => ""}
			onSuccess={() => {
				closeAndRefresh(refresh, close, modalCloseDelay)()
			}}
		>
			<div className="field">
				<label className="label">
					Tags <span className="has-text-grey-light">(Optional)</span>
				</label>
				<div className={classNames("edit-tags-modal-tags-container", (hasError || state.isErr()) && "has-error")}>
					{tags.map((tag, index) => (
						<TagPill key={`${tag.key}-${index}`} tag={tag} onRemove={() => handleRemoveTag(index)} />
					))}
					{isAddingTag && (
						<div className="edit-tags-modal-new-tag-inputs">
							<div className={classNames("edit-tags-modal-new-tag-key-wrapper", !newTagKey && "is-empty")}>
								<div className="edit-tags-modal-input-sizer-wrapper">
									<span className="edit-tags-modal-input-sizer">{newTagKey}</span>
									<input
										ref={keyInputRef}
										type="text"
										className="edit-tags-modal-new-tag-key"
										placeholder="Enter Key"
										value={newTagKey}
										maxLength={MAX_TAG_KEY_LENGTH}
										pattern="^[a-zA-Z0-9_]+$"
										onInvalid={(e) =>
											e.currentTarget.setCustomValidity("Only letters, numbers, and underscores are allowed")
										}
										onChange={(e) => setNewTagKey(e.target.value)}
										onKeyDown={handleKeyDown}
									/>
								</div>
							</div>
							<div className={classNames("edit-tags-modal-new-tag-value-wrapper", !newTagValue && "is-empty")}>
								<div className="edit-tags-modal-input-sizer-wrapper">
									<span className="edit-tags-modal-input-sizer">{newTagValue}</span>
									<input
										type="text"
										className="edit-tags-modal-new-tag-value"
										placeholder="Enter Value"
										value={newTagValue}
										maxLength={MAX_TAG_VALUE_LENGTH}
										onChange={(e) => setNewTagValue(e.target.value)}
										onKeyDown={handleKeyDown}
									/>
								</div>
								<button
									type="button"
									className="edit-tags-modal-confirm-add"
									onClick={handleAddTag}
									disabled={!newTagKey.trim() || !newTagValue.trim()}
								>
									<Icon icon="approve-v-check-validation" size={12} />
								</button>
								<button
									type="button"
									className="edit-tags-modal-cancel-add"
									onClick={() => {
										setIsAddingTag(false)
										setNewTagKey("")
										setNewTagValue("")
									}}
								>
									<Icon icon="delete-close-x" size={12} />
								</button>
							</div>
						</div>
					)}
					{!isMaxTagsReached && !isAddingTag && (
						<button type="button" className="edit-tags-modal-add-button" onClick={() => setIsAddingTag(true)}>
							<Icon icon="interface-add-1" size={12} />
							<span>Another Tag</span>
						</button>
					)}
				</div>
				{hasError && <p className="help is-danger">You can add up to {MAX_TAGS} tags</p>}
				{state.isErr() && (
					<p className="help is-danger">
						{state.error.errors?.[0]?.detail || state.error.errors?.[0]?.title || "Failed to update tags"}
					</p>
				)}
			</div>
		</AsyncResultModal>
	)
}

export default function EditTagsModal({originalTags, close, refresh, updateTags}: EditTagsModalProps) {
	return <Modal originalTags={originalTags} close={close} refresh={refresh} updateTags={updateTags} />
}
